import {
  EmbeddingModelV3,
  ImageModelV3,
  LanguageModelV3,
  ProviderV3,
  RerankingModelV3,
  SpeechModelV3,
  TranscriptionModelV3,
} from '@ai-sdk/provider';
import { AI302ImageModelId, AI302ImageSettings } from './ai302-image-settings';
import {
  FetchFunction,
  loadApiKey,
  withoutTrailingSlash,
  withUserAgentSuffix,
} from '@ai-sdk/provider-utils';
import { AI302ImageModel } from './ai302-image-model';
import { AI302Config } from './ai302-config';
import { AI302ChatSettings, AI302ChatModelId } from './ai302-chat-settings';
import { AI302EmbeddingModelId } from './ai302-embedding-settings';
import { AI302EmbeddingModel } from './ai302-embedding-model';
import { AI302LanguageModel } from './ai302-language-model';
import { AI302RerankingModel } from './ai302-reranking-model';
import { AI302RerankingModelId } from './ai302-reranking-settings';
import { AI302SpeechModel } from './ai302-speech-model';
import { AI302SpeechModelId } from './ai302-speech-settings';
import { AI302TranscriptionModel } from './ai302-transcription-model';
import { AI302TranscriptionModelId } from './ai302-transcription-settings';
import { VERSION } from './version';

export interface AI302ProviderSettings {
  /**
  AI302 API key. Default value is taken from the `AI302_API_KEY`
  environment variable.
  */
  apiKey?: string;
  /**
  Base URL for the API calls.
  */
  baseURL?: string;
  /**
  Custom headers to include in the requests.
  */
  headers?: Record<string, string>;
  /**
  Custom fetch implementation. You can use it as a middleware to intercept requests,
  or to provide a custom fetch implementation for e.g. testing.
  */
  fetch?: FetchFunction;
}

export interface AI302Provider extends ProviderV3 {
  /**
    Creates a model for text generation.
    */
  (modelId: AI302ChatModelId, settings?: AI302ChatSettings): LanguageModelV3;

  /**
Creates a chat model for text generation.
*/
  languageModel(
    modelId: AI302ChatModelId,
    settings?: AI302ChatSettings,
  ): LanguageModelV3;

  /**
Creates a chat model for text generation.
*/
  chatModel(
    modelId: AI302ChatModelId,
    settings?: AI302ChatSettings,
  ): LanguageModelV3;

  /**
  Creates a text embedding model.
  Use providerOptions in embed() call for model-specific options like dimensions.
  */
  textEmbeddingModel(modelId: AI302EmbeddingModelId): EmbeddingModelV3;

  /**
  Creates a text embedding model (alias for textEmbeddingModel).
  */
  embeddingModel(modelId: AI302EmbeddingModelId): EmbeddingModelV3;

  /**
  Creates a model for image generation.
  */
  image(
    modelId: AI302ImageModelId,
    settings?: AI302ImageSettings,
  ): ImageModelV3;

  /**
  Creates a model for image generation (alias for image).
  */
  imageModel(
    modelId: AI302ImageModelId,
    settings?: AI302ImageSettings,
  ): ImageModelV3;

  /**
  Creates a model for speech generation (TTS).
  @param modelId Format: "provider/voice" e.g. "openai/alloy", "azure/zh-CN-XiaoxiaoNeural"
  */
  speech(modelId: AI302SpeechModelId): SpeechModelV3;

  /**
  Creates a model for speech generation (alias for speech).
  */
  speechModel(modelId: AI302SpeechModelId): SpeechModelV3;

  /**
  Creates a model for reranking documents.
  */
  reranking(modelId: AI302RerankingModelId): RerankingModelV3;

  /**
  Creates a model for reranking documents (alias for reranking).
  */
  rerankingModel(modelId: AI302RerankingModelId): RerankingModelV3;

  /**
  Creates a model for audio transcription (speech-to-text).
  @param modelId The transcription model ID, e.g. "whisper-1", "gpt-4o-transcribe"
  */
  transcription(modelId: AI302TranscriptionModelId): TranscriptionModelV3;

  /**
  Creates a model for audio transcription (alias for transcription).
  */
  transcriptionModel(modelId: AI302TranscriptionModelId): TranscriptionModelV3;
}

const defaultBaseURL = 'https://api.302.ai';

export function createAI302(
  options: AI302ProviderSettings = {},
): AI302Provider {
  const baseURL = withoutTrailingSlash(options.baseURL) ?? defaultBaseURL;

  // Lazy API key resolution - only load when actually needed (during API calls)
  const getApiKey = () =>
    loadApiKey({
      apiKey: options.apiKey,
      environmentVariableName: 'AI302_API_KEY',
      description: '302 AI API key',
    });

  const getHeaders = () =>
    withUserAgentSuffix(
      {
        Authorization: `Bearer ${getApiKey()}`,
        ...options.headers,
      },
      `ai-sdk/ai302/${VERSION}`,
    );

  // Midjourney-specific headers (includes mj-api-secret)
  const getMidjourneyHeaders = () => {
    const apiKey = getApiKey();
    return withUserAgentSuffix(
      {
        Authorization: `Bearer ${apiKey}`,
        'mj-api-secret': apiKey,
        ...options.headers,
      },
      `ai-sdk/ai302/${VERSION}`,
    );
  };

  const getCommonModelConfig = (modelType: string): AI302Config => ({
    provider: `ai302.${modelType}`,
    url: ({ modelId, path }) => {
      if (modelType === 'embedding') {
        if (modelId.includes('jina')) {
          return `${baseURL}/jina/v1${path}`;
        }
        return `${baseURL}/v1${path}`;
      }
      return `${baseURL}${path}`;
    },
    headers: getHeaders,
    midjourneyHeaders: getMidjourneyHeaders,
    fetch: options.fetch,
  });

  const createImageModel = (
    modelId: AI302ImageModelId,
    settings?: AI302ImageSettings,
  ) => {
    return new AI302ImageModel(
      modelId,
      settings ?? {},
      getCommonModelConfig('image'),
    );
  };

  const createChatModel = (
    modelId: AI302ChatModelId,
    settings: AI302ChatSettings = {},
  ) => {
    return new AI302LanguageModel(modelId, settings, getCommonModelConfig('chat'));
  };

  const createTextEmbeddingModel = (modelId: AI302EmbeddingModelId) => {
    return new AI302EmbeddingModel(modelId, getCommonModelConfig('embedding'));
  };

  const createSpeechModel = (modelId: AI302SpeechModelId) => {
    return new AI302SpeechModel(modelId, getCommonModelConfig('speech'));
  };

  const createRerankingModel = (modelId: AI302RerankingModelId) => {
    return new AI302RerankingModel(modelId, getCommonModelConfig('reranking'));
  };

  const createTranscriptionModel = (modelId: AI302TranscriptionModelId) => {
    return new AI302TranscriptionModel(modelId, getCommonModelConfig('transcription'));
  };

  const provider = (modelId: AI302ChatModelId, settings?: AI302ChatSettings) =>
    createChatModel(modelId, settings);

  provider.specificationVersion = 'v3' as const;
  provider.languageModel = createChatModel;
  provider.chatModel = createChatModel;
  provider.textEmbeddingModel = createTextEmbeddingModel;
  provider.embeddingModel = createTextEmbeddingModel;
  provider.image = createImageModel;
  provider.imageModel = createImageModel;
  provider.speech = createSpeechModel;
  provider.speechModel = createSpeechModel;
  provider.reranking = createRerankingModel;
  provider.rerankingModel = createRerankingModel;
  provider.transcription = createTranscriptionModel;
  provider.transcriptionModel = createTranscriptionModel;

  return provider as AI302Provider;
}

export const ai302 = createAI302();
